import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { money, typeLabel } from "@/components/shop/common";

export default function PrintWorkOrder() {
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/work-orders/${id}`).then((r) => setWo(r.data));
    api.get("/settings").then((r) => setS(r.data));
  }, [id]);
  if (!wo || !s) return null;
  const isInv = wo.status === "invoiced";

  return (
    <div className="min-h-screen bg-white text-slate-900 print:p-0 p-6" data-testid="print-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between mb-6 print:hidden">
          <Link to={`/app/work-orders/${id}`} className="flex items-center gap-1 text-sm text-slate-600" data-testid="print-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
          <button onClick={() => window.print()} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-sm text-sm font-semibold" data-testid="print-btn"><Printer className="h-4 w-4" /> Print / Save PDF</button>
        </div>
        <div className="flex justify-between items-start border-b-4 border-orange-500 pb-4">
          <div>
            <h1 className="text-3xl font-extrabold uppercase">{s.shop_name}</h1>
            <p className="text-sm text-slate-600">{s.address}</p><p className="text-sm text-slate-600">{s.phone}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold uppercase" data-testid="print-doc-type">{isInv ? "Invoice" : "Estimate"}</p>
            <p className="font-mono text-sm">{isInv ? `INV #${wo.invoice_number}` : `WO #${wo.number}`}</p>
            <p className="text-sm text-slate-600">{new Date(wo.invoiced_at || wo.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 py-5 text-sm">
          <div><p className="text-xs uppercase text-slate-500 font-semibold">Customer</p><p className="font-semibold">{wo.customer?.name}</p><p>{wo.customer?.company}</p><p>{wo.customer?.phone}</p><p>{wo.customer?.email}</p></div>
          <div><p className="text-xs uppercase text-slate-500 font-semibold">Vehicle</p><p className="font-semibold">{wo.vehicle_label}</p><p>{typeLabel(wo.vehicle?.type)}</p><p>{wo.vehicle?.vin && `VIN ${wo.vehicle.vin}`}</p><p>{wo.vehicle?.odometer && `Odometer/Hours ${wo.vehicle.odometer}`}</p></div>
        </div>
        {["complaint", "cause", "correction"].map((k) => wo[k] && (
          <p key={k} className="text-sm mb-2"><span className="uppercase text-xs font-semibold text-slate-500 mr-2">{k}:</span>{wo[k]}</p>
        ))}
        <table className="w-full text-sm mt-5">
          <thead><tr className="border-b-2 border-slate-900 text-left text-xs uppercase"><th className="py-2">Description</th><th className="px-3">Qty/Hrs</th><th className="text-right px-3 w-28">Rate</th><th className="text-right w-28">Amount</th></tr></thead>
          <tbody>
            {wo.labor_lines.map((l) => <tr key={l.id} className="border-b border-slate-200"><td className="py-2">Labor: {l.description}</td><td className="px-3">{l.hours}</td><td className="text-right px-3">{money(l.rate)}</td><td className="text-right">{money(l.hours * l.rate)}</td></tr>)}
            {wo.part_lines.map((p) => <tr key={p.id} className="border-b border-slate-200"><td className="py-2">{p.description} {p.part_number && <span className="font-mono text-xs text-slate-500">({p.part_number})</span>}</td><td className="px-3">{p.qty}</td><td className="text-right px-3">{money(p.unit_price)}</td><td className="text-right">{money(p.qty * p.unit_price)}</td></tr>)}
          </tbody>
        </table>
        <div className="ml-auto w-64 mt-4 text-sm space-y-1">
          <p className="flex justify-between"><span>Labor</span><span>{money(wo.labor_total)}</span></p>
          <p className="flex justify-between"><span>Parts</span><span>{money(wo.parts_total)}</span></p>
          <p className="flex justify-between"><span>Tax</span><span>{money(wo.tax)}</span></p>
          <p className="flex justify-between text-xl font-bold border-t-2 border-slate-900 pt-2" data-testid="print-total"><span>Total</span><span>{money(wo.total)}</span></p>
        </div>
        <p className="text-xs text-slate-500 mt-12">Thank you for your business.</p>
      </div>
    </div>
  );
}
